// „Не стига наличност за „Шопска салата"" — въпросът при запис (Ctrl+Enter),
// когато съставка на неразделена рецепта не стига КЪМ ДАТАТА. Общият
// потвърждаващ диалог (components/confirm) с преглед „кое, колко трябва, колко
// има" и двата естествени хода като избори-карти: „Намали на N порции"
// (колкото стигат при останалите редове — основният, когато има такива) и
// „Раздели на продукти" (съставките стават редове, които се поправят
// поотделно). Esc/„Отказ" връща в порциите на реда. Нищо не се записва оттук.
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ConfirmDialog } from "../../components/confirm/ConfirmDialog";
import type { ConfirmChoice, ConfirmNote } from "../../components/confirm/confirmTypes";
import { portionsLabel } from "./writeoffConfirms";
import { ShortageAmounts } from "./WriteoffShortageBand";
import type { RecipeRowStock } from "./useWriteoffStock";
import { shortageMissingText } from "./writeoffStockCheck";
import { parseDecimal, type WriteoffLineDraft } from "./writeoffWorksheetShared";

type Props = {
  open: boolean;
  line: WriteoffLineDraft | null;
  stock: RecipeRowStock | undefined;
  // към коя дата и в кой склад е наличността
  dateLabel: string;
  warehouseName: string;
  onFit: (portions: number) => void;
  onSplit: () => void;
  onCancel: () => void;
  afterClose: () => void;
};

export const WriteoffShortageDialog = ({
  open,
  line,
  stock,
  dateLabel,
  warehouseName,
  onFit,
  onSplit,
  onCancel,
  afterClose,
}: Props) => {
  const { t } = useTranslation();
  const shortages = stock?.shortages ?? [];
  const portions = line ? parseDecimal(line.quantity) : 0;
  const fit = stock?.fittingPortions ?? null;
  const canFit = fit != null && fit > 0 && fit < portions;

  const choices: ConfirmChoice[] = [];
  if (canFit)
    choices.push({
      key: "fit",
      label: t("inventoryPage.wr6FitAction", { portions: portionsLabel(t, fit) }),
      hint: t("inventoryPage.wr6DialogFitHint"),
      primary: true,
      onSelect: () => onFit(fit),
    });
  choices.push({
    key: "split",
    label: t("inventoryPage.splitToProducts"),
    hint: t("inventoryPage.wr6DialogSplitHint", { count: stock?.ingredientCount ?? 0 }),
    primary: !canFit,
    onSelect: onSplit,
  });
  const notes: ConfirmNote[] = [];
  if (fit === 0) notes.push({ kind: "info", text: t("inventoryPage.wr6DialogNoFit") });

  return (
    <ConfirmDialog
      open={open}
      intent="warning"
      icon={<ExclamationCircleOutlined />}
      title={t("inventoryPage.wr6DialogTitle", { name: line?.recipeName ?? "" })}
      explanation={t("inventoryPage.wr6DialogText", {
        count: shortages.length,
        portions: portionsLabel(t, portions),
        date: dateLabel,
        warehouse: warehouseName,
      })}
      preview={{
        caption: t("inventoryPage.wr6DialogCaption"),
        items: shortages.map((shortage) => ({
          key: shortage.productId,
          label: shortage.name,
          hint: line ? <ShortageAmounts shortage={shortage} lineKey={line.key} /> : null,
          value: shortageMissingText(shortage),
          tone: "danger",
        })),
        limit: 8,
      }}
      choices={choices}
      note={notes}
      onCancel={onCancel}
      afterClose={afterClose}
    />
  );
};

export default WriteoffShortageDialog;
